import { PropsWithChildren, memo, useCallback, useEffect, useRef } from 'react';
import { RootState } from 'types';
import { EntityTypes } from 'constants/entities';
import { selectOfflineId } from 'core/selectors/admin/offlineId';
import { useSelector } from 'react-redux';
import { IText } from 'types/domain/text';
import { update } from 'core/actions/admin/data';
import { debounce } from 'lodash';
import { useAppDispatch } from 'core/store/hooks';
import InputField from '../InputField';
import TextArea from '../TextArea';
import { ActionEntityPosition, ActionStyle } from 'types/domain/action';
import { LexicalTextEditor } from './Lexical';
import { Markup } from 'interweave';
import RenderHtml from './Slate/renderHtml';
import { CourseNodeRole } from 'types/domain/course';
import { selectCourseUserVariables } from 'core/selectors/admin/courseVariables';
import { selectVariableRegister } from 'core/selectors/product/variableRegister';

export enum TextType {
  SHORT_TEXT = 0,
  HTML = 1,
  AREA = 2,
}

interface Props extends PropsWithChildren {
  id?: string;
  textId: number;
  parentId?: number;
  parentRef?: string;
  parentType?: EntityTypes;
  moduleId?: number;
  actionId?: number;
  className?: string;
  readOnly?: boolean;
  inScene?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  type?: TextType;
  text?: IText;
  position?: ActionEntityPosition;
  style?: ActionStyle;
  canSelectVariable?: boolean;
  onChange?: (val) => void;
  onClick?: (val) => void;
  onFocus?: (val) => void;
  onBlur?: (val) => void;
  onDelete?: (val) => void;
  onExpand?: (val) => void;
  onPosition?: (val) => void;
  onStyle?: (val) => void;
}

export const TextEditor = memo(
  ({
    id,
    className,
    textId,
    parentId,
    moduleId,
    actionId,
    readOnly = false,
    parentType,
    inScene = false,
    autoFocus = false,
    canSelectVariable = false,
    placeholder,
    onChange,
    onFocus,
    onBlur,
    type = TextType.HTML,
    text,
    onClick,
    onDelete,
    onExpand,
    onPosition,
    onStyle,
    style,
    children,
    parentRef,
    position,
  }: Props) => {
    const offlineId = useSelector(selectOfflineId);
    const dispatch = useAppDispatch();
    const model: IText = useSelector((state: RootState) =>
      textId ? state.entity.present[EntityTypes.TEXT][textId] : text,
    );

    const variableRegister = useSelector(selectVariableRegister);

    const prevTextId = useRef<number>(null);
    const parentEntityId = useRef<number>(null);
    const parentEntityType = useRef<EntityTypes>(null);
    const parentModuleId = useRef<number>(null);
    const parentActionId = useRef<number>(null);

    const debouncedUpdateRef = useRef<any>(value => {
      let entity: any = {
        id: prevTextId.current || offlineId,
        offlineId,
        moduleId: parentModuleId.current,
        actionId: parentActionId.current,
      };

      if (type === TextType.HTML) {
        entity.html = value.html;
        entity.htmlJson = value.htmlJson;
        entity.type = 1;
      } else entity.shortText = value;

      if (onChange) onChange(entity);
      else if (entity && parentEntityId.current > 0)
        dispatch(
          update({
            type: EntityTypes.TEXT,
            parentEntityType: parentEntityType.current,
            parentEntityId: parentEntityId.current,
            parentModuleId: parentModuleId.current,
            parentEntityAttribute: 'textId',
            entity,
            //persist: textId ? textId > 0 : true,
          }),
        );
    });

    const onChangeColor = useCallback(
      color => {
        dispatch(
          update({
            type: EntityTypes.TEXT,
            parentEntityType: parentEntityType.current,
            parentEntityId: parentEntityId.current,
            parentModuleId: parentModuleId.current,
            parentEntityAttribute: 'textId',
            entity: { id: textId, color },
            //persist: textId ? textId > 0 : true,
          }),
        );
      },
      [dispatch, textId],
    );

    useEffect(() => {
      if (readOnly) return;

      if (!model) {
        prevTextId.current = null;
        return;
      }

      if (model.offlineId && model.offlineId === prevTextId.current) {
        prevTextId.current = textId;
        return;
      } else prevTextId.current = textId;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [textId, type, readOnly]);

    useEffect(() => {
      parentEntityId.current = parentId;
      parentEntityType.current = parentType;
      parentModuleId.current = moduleId;
      parentActionId.current = actionId;
    }, [parentId, parentType, moduleId, actionId]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debounceUpdate = useCallback(
      debounce(value => debouncedUpdateRef.current(value), 1000),
      [],
    );

    const updateText = useCallback(
      val => {
        debounceUpdate(val);
      },
      [debounceUpdate],
    );

    const replaceVariables = useCallback(html => {
      if (!html) return html;
      const regex = /\[var_[0-9]+\]/g;
      const matches = html.match(regex);

      if (matches) {
        matches.forEach(match => {
          const variable = match.match(/\d+/g);
          html = html.replace(match, variableRegister[variable]?.value ?? 0);
        });
      }
      return html;
    }, []);

    if (type === TextType.SHORT_TEXT)
      return (
        <InputField
          name={'name'}
          value={model?.shortText || ''}
          autoFocus={autoFocus}
          placeholder={placeholder}
          onChange={updateText}
          readOnly={readOnly}
          className={className}
          inputClassName={className}
          onFocus={e => (onFocus ? onFocus(e) : null)}
          onBlur={e => (onBlur ? onBlur(e) : null)}
        />
      );

    if (type === TextType.AREA) {
      if (readOnly)
        return (
          <span onClick={e => (onClick ? onClick : {})} className={`${className}`}>
            {model?.shortText || ''}
          </span>
        );
      return (
        <TextArea
          className={`${className}`}
          value={model?.shortText}
          onChange={updateText}
          autoFocus={autoFocus}
          readOnly={readOnly}
          onFocus={e => (onFocus ? onFocus(e) : {})}
          onBlur={e => (onBlur ? onBlur(e) : {})}
        />
      );
    }

    if (readOnly)
      return (
        <div onClick={e => (onClick ? onClick(e) : {})}>
          <Markup tagName="div" className={className} content={replaceVariables(model?.html)} />
        </div>
      );

    return (
      <LexicalTextEditor
        placeholder={placeholder}
        parentId={parentId}
        inScene={inScene}
        className={className}
        parentRef={parentRef}
        autoFocus={autoFocus}
        text={model}
        onBlur={onBlur}
        onFocus={onFocus}
        onChange={updateText}
        onChangeColor={
          parentType === EntityTypes.ACTION_ENTITY || (parentType === EntityTypes.ACTION && !!onPosition)
            ? onChangeColor
            : null
        }
        onDelete={onDelete}
        onExpand={onExpand}
        position={position}
        onPosition={onPosition}
        onStyle={onStyle}
        style={style}
        canSelectVariable={canSelectVariable}
      />
    );
  },
);

export default TextEditor;
